import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Tabs,
  Tab,
  Container
} from '@mui/material';
import { supabase } from '../supabaseClient';
import { AuthContext } from '../context/AuthContext';
import SuggestionDialog from '../components/suggestions/SuggestionDialog';
import SuggestionsTable from '../components/suggestions/SuggestionsTable';
import { useTheme as useMuiTheme } from '@mui/material/styles';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function StofSuggesties() {
  const { user, role } = useContext(AuthContext);
  const userId = user?.id;
  const [unmatched, setUnmatched] = useState([]);
  const [pendingSuggestions, setPendingSuggestions] = useState([]);
  const [handledSuggestions, setHandledSuggestions] = useState([]);
  const [loadingUnmatched, setLoadingUnmatched] = useState(true);
  const [loadingSuggestions, setLoadingSuggestions] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedStof, setSelectedStof] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const muiTheme = useMuiTheme();

  const fetchUnmatched = useCallback(async () => {
    setLoadingUnmatched(true);
    const { data, error } = await supabase.rpc('get_unmatched_stof_values');
    if (error) {
      console.error('Error fetching unmatched substances:', error);
      setError(error.message);
    } else {
      setUnmatched(data || []);
    }
    setLoadingUnmatched(false);
  }, []);

  const fetchSuggestions = useCallback(async () => {
    if (!userId) return;
    setLoadingSuggestions(true);
    setError(null);

    let pendingQuery = supabase
      .from('stof_value_quality_suggestions')
      .select('*, suggested_parameter:suggested_parameter_id(description), author:added_by_user_id(full_name)')
      .eq('status', 'pending');
    
    let handledQuery = supabase
      .from('stof_value_quality_suggestions')
      .select('*, suggested_parameter:suggested_parameter_id(description), author:added_by_user_id(full_name), approver:approved_by_user_id(full_name)')
      .eq('status', 'approved');

    if (role !== 'admin') {
      pendingQuery = pendingQuery.eq('added_by_user_id', userId);
      handledQuery = handledQuery.eq('added_by_user_id', userId);
    }
    
    const { data: pending, error: pError } = await pendingQuery.order('created_at', { ascending: false });
    const { data: handled, error: hError } = await handledQuery.order('updated_at', { ascending: false });

    if (pError || hError) {
      setError(pError?.message || hError?.message);
    } else {
      setPendingSuggestions(pending || []);
      setHandledSuggestions(handled || []);
    }
    setLoadingSuggestions(false);
  }, [userId, role]);

  useEffect(() => {
    if (userId) {
      fetchUnmatched();
      fetchSuggestions();
    }
  }, [userId, fetchUnmatched, fetchSuggestions]);

  const handleApprove = async (suggestionId) => {
    const { error } = await supabase.rpc('approve_stof_suggestion', { p_suggestion_id: suggestionId });
    if (error) {
      console.error('Error approving suggestion:', error);
      setError(error.message);
    } else {
      fetchUnmatched();
      fetchSuggestions();
    }
  };

  const handleReject = async (suggestionId) => {
    const { error } = await supabase.rpc('reject_stof_suggestion', { p_suggestion_id: suggestionId });
    if (error) {
      console.error('Error rejecting suggestion:', error);
      setError(error.message);
    } else {
      fetchSuggestions();
    }
  };

  const handleSuggest = (stofValue) => {
    setSelectedStof(stofValue);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setSelectedStof(null);
    setDialogOpen(false);
  };

  const handleSubmitSuggestion = async () => {
    handleCloseDialog();
    fetchUnmatched();
    fetchSuggestions();
  };

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };
  
  if (error) {
    return <Alert severity="error" sx={{ mt: 2 }}>Fout bij het ophalen van gegevens: {error}</Alert>;
  }


  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <Paper elevation={0} sx={{
        background: `linear-gradient(135deg, ${muiTheme.palette.primary.main} 0%, ${muiTheme.palette.primary.dark} 100%)`,
        color: 'primary.contrastText',
        py: 4,
        px: { xs: 2, md: 4 },
        mb: 2,
        borderRadius: 0,
        textAlign: 'center',
        width: '100vw',
        position: 'relative',
        left: '50%',
        right: '50%',
        marginLeft: '-50vw',
        marginRight: '-50vw',
        minWidth: '100vw',
        maxWidth: '100vw',
      }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Stofsuggesties
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.9 }}>
          Beheer en koppel stofsuggesties aan parameters
        </Typography>
      </Paper>
      <Container maxWidth="lg" sx={{ mt: 2 }}>
        <Paper elevation={2} sx={{ p: { xs: 2, md: 4 }, borderRadius: 3 }}>
       
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabIndex} onChange={handleTabChange} aria-label="my suggestions tabs">
              <Tab label="Openstaand" />
              <Tab label="Ingediend" />
              <Tab label="Verwerkt" />
            </Tabs>
          </Box>
          <TabPanel value={tabIndex} index={0}>
            <Typography variant="body1" gutterBottom sx={{mt: 2}}>
              Hieronder staat een lijst van stoffen die in documenten zijn gevonden maar nog niet gekoppeld zijn. Maak een suggestie om deze te koppelen.
            </Typography>
            {loadingUnmatched ? <CircularProgress sx={{mt: 2}} /> : (
              <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Gevonden Stofnaam</TableCell>
                      <TableCell align="right">Aantal keer gevonden</TableCell>
                      <TableCell align="center">Acties</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {unmatched.map((item) => (
                      <TableRow key={item.stof_value}>
                        <TableCell component="th" scope="row">
                          {item.stof_value}
                        </TableCell>
                        <TableCell align="right">{item.occurrence_count}</TableCell>
                        <TableCell align="center">
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleSuggest(item.stof_value)}
                          >
                            Koppel Suggestie
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>
          <TabPanel value={tabIndex} index={1}>
            <SuggestionsTable
              suggestions={pendingSuggestions}
              isLoading={loadingSuggestions}
              role={role}
              {...(role === 'admin' && { onApprove: handleApprove, onReject: handleReject })}
            />
          </TabPanel>
          <TabPanel value={tabIndex} index={2}>
            <SuggestionsTable
              suggestions={handledSuggestions}
              isLoading={loadingSuggestions}
              role={role}
              showStatus={true}
            />
          </TabPanel>
          <SuggestionDialog
            open={dialogOpen}
            onClose={handleCloseDialog}
            onSubmit={handleSubmitSuggestion}
            stofValue={selectedStof}
          />
        </Paper>
      </Container>
    </Box>
  );
}

export default StofSuggesties; 